import React from 'react'

import { SimpleTable, TableSkeleton, Alert } from '@/components/ui'
import { useApi, useDashboard, useSkeletonOnConfigChange } from '@/hooks'
import useCompany from '@/hooks/useCompany'
import { validateDateRangeWithin30Days } from '@/insights/common/formatUtils'
import { buildQuery } from '@/insights/common/queryUtils'
import Caption, { addDateToCaptionFields } from '@/insights/components/Caption'
import { NonAlarmDateOptionsWithCustom } from '@/insights/components/CustomSingleSelect'
import {
  ApiService,
  type Asset,
  AssetTypeFilter,
  type InsightDefinition,
  type InsightParametersType,
  InsightStatusCode,
  isInsightStatus,
} from '@/types/dashboard-types'
import { celsiusToUser } from '@/utils/conversions'
import { downloadCsv } from '@/utils/csvExport'
import { formatUserDate, standardDateTimeFormat } from '@/utils/formatDate'

interface InStoreTemperatureHistoryParams extends InsightParametersType {
  companyId?: number
  assetId?: string
  date?: string
  from?: string
  to?: string
}

interface InStoreTemperatureHistory {
  id: number
  companyId: number
  umdId: number
  powerState: string
  powerMode: string
  operation: string
  zone1Mode: string
  zone1SP: number
  zone1SA: number
  zone1RA: number
  zone2Mode: string
  zone2SP: number
  zone2SA: number
  zone2RA: number
  zone3Mode: string
  zone3SP: number
  zone3SA: number
  zone3RA: number
  fridgeSampleTime: Date
  locationSampleTime: Date
  ambientTemperatureCelsius: number
  assetId: number
  assetName: string
  mp1: number
  mp2: number
  mp3: number
  mp4: number
  mp5: number
  mp6: number
  mp7: number
  mp8: number
  mp9: number
  mp10: number
  commonSampleTime: Date
  monitoringPointNames: string
  zone1VSP?: number
  zone2VSP?: number
  zone3VSP?: number
}

const InStoreTemperatureHistory: InsightDefinition<InStoreTemperatureHistoryParams, InStoreTemperatureHistory[]> = {
  active: true,
  slug: 'temperature/in-store-history',
  category: 'temperature',
  name: 'InStore Temperature History',
  description: 'Display temperature history for an InStore asset for a given date range',
  defaultWidth: 'full',
  defaultAspectRatio: 16 / 9,

  configuration: {
    controls: [
      { id: 'companyId', control: 'CompanyDropdown', required: true },
      {
        id: 'assetId',
        control: 'AssetDropdownSingle',
        required: true,
        assetType: AssetTypeFilter.InStore,
        label: 'Location',
      },
      {
        id: 'date',
        control: 'CustomSingleSelect',
        options: NonAlarmDateOptionsWithCustom,
      },
      {
        id: 'customDate',
        label: 'Date',
        control: 'DateTimeRange',
        dependsOn: 'date',
        dependsOnValue: 'custom',
      },
    ],

    isValid: (configuration: InStoreTemperatureHistoryParams) => {
      if (!configuration?.companyId || !configuration.assetId) {
        return 'Company and Asset are required.'
      }

      if (configuration.date === 'custom') {
        if (!configuration.from || !configuration.to) {
          return 'Enter the custom date range'
        }
        const fromdate = new Date(configuration.from)
        const todate = new Date(configuration.to)
        if (fromdate > todate) {
          return 'Start date must be before end date'
        }
        const dateValidation30Days = validateDateRangeWithin30Days(fromdate.toISOString(), todate.toISOString())
        if (dateValidation30Days !== true) {
          return dateValidation30Days
        }
      }

      return true
    },
  },

  load: async (api: ApiService, parameters: InStoreTemperatureHistoryParams) => {
    const path = `/insights/temperature/history?${buildQuery(parameters)}`
    const response = await api.get<InStoreTemperatureHistory[]>({
      path,
      origin: 'InStoreTemperatureHistory',
    })
    if (response.error) {
      return { code: InsightStatusCode.ERROR, message: response.message }
    }
    if (!response.data) {
      return { code: InsightStatusCode.NO_DATA }
    }
    return response.data
  },

  csv: async (api: ApiService, parameters: InStoreTemperatureHistoryParams, title: string) => {
    const path = '/insights/temperature/history'
    await downloadCsv(api, path, { ...parameters, includeLocation: 'false' }, title)
  },

  Display: ({ status, data, configuration }) => {
    const showSkeleton = useSkeletonOnConfigChange({ status, data, configuration })
    console.log('InStoreTemperatureHistory Display', { status, data, configuration, showSkeleton })
    const units = useDashboard().configuration.units as 'Imperial' | 'Metric'
    if (status.code === InsightStatusCode.NOT_CONFIGURED) {
      return <Alert type="info" message={status.message || 'Insight has not been configured'} />
    }
    if (showSkeleton) {
      return <TableSkeleton columns={16} withHeader rowHeight={35} />
    }
    if (status.code !== InsightStatusCode.DISPLAYING && status.code !== InsightStatusCode.LOADING) {
      return null
    }
    if (isInsightStatus(data)) {
      return null
    }
    if (!data?.length) {
      return null
    }

    type HeaderType = { key: string; title: string; size: number } | string
    const headers: HeaderType[] = [{ key: 'Date', title: 'Date', size: 200 }]
    let hasZone1 = false,
      hasZone2 = false,
      hasZone3 = false,
      hasVsp1 = false,
      hasVsp2 = false,
      hasVsp3 = false,
      hasReefer = false
    let mpCount = 0
    data.forEach((sample) => {
      const count = Object.keys(sample)
        .filter((k) => k.startsWith('mp'))
        .reduce((sum, k) => {
          if (sample[k as keyof InStoreTemperatureHistory]) {
            sum += 1
          }
          return sum
        }, 0)
      if (count > mpCount) {
        mpCount = count
      }
      if (sample.zone1Mode || sample.zone1RA || sample.zone1SA) {
        hasZone1 = true
      }
      if (sample.zone2Mode || sample.zone2RA || sample.zone2SA) {
        hasZone2 = true
      }
      if (sample.zone3Mode || sample.zone3RA || sample.zone3SA) {
        hasZone3 = true
      }
      if (sample.zone1VSP) {
        hasVsp1 = true
      }
      if (sample.zone2VSP) {
        hasVsp2 = true
      }
      if (sample.zone3VSP) {
        hasVsp3 = true
      }
      if (sample.fridgeSampleTime) {
        hasReefer = true
      }
    })
    if (hasReefer) {
      headers.push('Reefer Status')
      headers.push('Ambient Temp')
    }
    if (hasZone1) {
      headers.push('Z1 Mode')
      headers.push('Z1 SP')
      headers.push('Z1 SA')
      headers.push('Z1 RA')
    }
    if (hasVsp1) {
      headers.push('Z1 VSP')
    }
    if (hasZone2) {
      headers.push('Z2 Mode')
      headers.push('Z2 SP')
      headers.push('Z2 SA')
      headers.push('Z2 RA')
    }
    if (hasVsp2) {
      headers.push('Z2 VSP')
    }
    if (hasZone3) {
      headers.push('Z3 Mode')
      headers.push('Z3 SP')
      headers.push('Z3 SA')
      headers.push('Z3 RA')
    }
    if (hasVsp3) {
      headers.push('Z3 VSP')
    }
    const mpNames = data[0].monitoringPointNames.split(',')
    headers.push(...mpNames.filter((name) => name !== ''))
    const body = data.map((sample) => {
      const temp: Record<string, string | number> = {
        Date: formatUserDate(sample.commonSampleTime, standardDateTimeFormat),
      }
      if (hasReefer) {
        temp['Reefer Status'] = sample.powerState ?? ''
        temp['Ambient Temp'] = celsiusToUser(sample.ambientTemperatureCelsius, 1, units)
      }
      if (hasZone1) {
        Object.assign(temp, {
          'Z1 Mode': sample.zone1Mode ?? '',
          'Z1 SP': celsiusToUser(sample.zone1SP, 1, units),
          'Z1 SA': celsiusToUser(sample.zone1SA, 1, units),
          'Z1 RA': celsiusToUser(sample.zone1RA, 1, units),
        })
      }
      if (hasVsp1) {
        Object.assign(temp, { 'Z1 VSP': sample.zone1VSP ? celsiusToUser(sample.zone1VSP, 0, units) : '' })
      }
      if (hasZone2) {
        Object.assign(temp, {
          'Z2 Mode': sample.zone2Mode ?? '',
          'Z2 SP': celsiusToUser(sample.zone2SP, 1, units),
          'Z2 SA': celsiusToUser(sample.zone2SA, 1, units),
          'Z2 RA': celsiusToUser(sample.zone2RA, 1, units),
        })
      }
      if (hasVsp2) {
        Object.assign(temp, { 'Z2 VSP': sample.zone2VSP ? celsiusToUser(sample.zone2VSP, 0, units) : '' })
      }
      if (hasZone3) {
        Object.assign(temp, {
          'Z3 Mode': sample.zone3Mode ?? '',
          'Z3 SP': celsiusToUser(sample.zone3SP, 1, units),
          'Z3 SA': celsiusToUser(sample.zone3SA, 1, units),
          'Z3 RA': celsiusToUser(sample.zone3RA, 1, units),
        })
      }
      if (hasVsp3) {
        Object.assign(temp, { 'Z3 VSP': sample.zone3VSP ? celsiusToUser(sample.zone3VSP, 0, units) : '' })
      }
      mpNames.forEach((mp, idx) => {
        temp[mp] = celsiusToUser(sample[`mp${idx + 1}` as keyof InStoreTemperatureHistory] as number, 1, units)
      })
      return temp
    })
    return (
      <section className="text-base-content text-base h-full flex flex-col">
        <div className="min-h-0 flex-1">
          <SimpleTable headers={headers} body={body} cellClassNames={{ Date: 'text-nowrap' }} />
        </div>
      </section>
    )
  },

  Caption: ({ configuration, status }) => {
    const [asset, setAsset] = React.useState<Asset>()
    const { companies } = useCompany()
    const { api } = useApi()
    const params = configuration.parameters as InStoreTemperatureHistoryParams
    React.useEffect(() => {
      if (!api) {
        return
      }
      const fn = async () => {
        if (params.assetId) {
          const response = await api.get<Asset[]>({
            path: `/fresh/assets?companyId=${params.companyId}&id=${params.assetId}&deleted=false`,
            origin: 'InStoreTemperatureHistory#caption',
          })
          if (response.data?.length) {
            const asset = response.data[0]
            setAsset(asset)
          } else {
            setAsset({ id: Number(params.assetId) } as Asset)
          }
        }
      }
      fn()
    }, [params])

    if (status.code === InsightStatusCode.ERROR) {
      return ''
    }
    const fields: Record<string, string> = {}
    if (params.companyId) {
      const company = companies.find((c) => c.id === params.companyId)
      if (company) {
        fields['Company'] = company.name
      }
    }
    if (asset) {
      fields['Asset'] = asset.name
    }
    addDateToCaptionFields({ ...params, fields, finite: true })
    if (Object.keys(fields).length) {
      return <Caption fields={fields} />
    }
    return ''
  },

  Footer: ({ data, status }) => {
    if (status.code === InsightStatusCode.LOADING || (status.code === InsightStatusCode.ERROR && data === undefined)) {
      return ''
    }
    if (status.code === InsightStatusCode.NO_DATA) {
      return <div>No records found</div>
    }
    const rows = data?.length
    if (!rows) {
      return <div>No records found</div>
    }
    return (
      <div>
        {rows} row{rows === 1 ? '' : 's'} found
      </div>
    )
  },
}

export default InStoreTemperatureHistory
